import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Outlet } from "react-router";
import "./Home.scss";
import { Link as LinkRouter, useLocation, useNavigate } from "react-router-dom";
import Logo from "../../assets/icons/logo-home.svg";
import LanguageIcon from "../../assets/img/language.svg";
import { useTranslation, getI18n } from "react-i18next";
import i18n from "i18next";
import { getStorageItem, setStorageItem } from "../../utils/localStorage";
import getClasses from "../../utils/getClasses";
import { useComponentVisible, setHeaderRoots } from "../../utils/functions";
import Chevron from "../../components/Forms/FormInput/FormSelect/Chevron/Chevron";
import { RoutesRouter } from "../../models/Routes";

interface LinkItemProps {
  name: string;
  icon: string | JSX.Element;
  link?: string;
  children?: { name: string; icon: string | JSX.Element; link: string }[];
}

export type Parameters = "edit" | "template" | "questions" | "survey";

const Home = () => {
  const { t } = useTranslation();

  const LinkItems: Array<LinkItemProps> = useMemo(() => {
    let output: Array<LinkItemProps> = [
      {
        name: t("home.companies.companies"),
        icon: "icon-company",
        link: "/home/companies",
      },
      {
        name: t("home.survey.survey"),
        icon: "icon-clipboard",
        children: [
          {
            name: t("home.survey.template.title"),
            icon: "",
            link: "/home/survey/template",
          },
          {
            name: t("home.survey.questions.title"),
            icon: "",
            link: "/home/survey/questions",
          },
        ],
      },
      {
        name: "Library",
        icon: "icon-book-open",
        link: "/home/library",
      },
      {
        name: "Issue",
        icon: "icon-issue",
        link: "/home/issue",
      },
    ];
    return output;
  }, [t]);

  const [currentLanguage, setCurrentLanguage] = useState("it");

  useEffect(() => {
    const language = getI18n().language;
    setCurrentLanguage(language === "it-IT" ? "it" : language);
  }, []);

  return (
    <div className="container-home">
      <SidebarContent LinkItems={LinkItems} />
      <div className="container-right">
        <MobileNav
          currentLanguage={currentLanguage}
          setLanguage={setCurrentLanguage}
        />
        {<Outlet></Outlet>}
      </div>
    </div>
  );
};

interface SidebarProps {
  LinkItems: Array<LinkItemProps>;
}

const SidebarContent = ({ LinkItems, ...rest }: SidebarProps) => {
  const { pathname } = useLocation();
  const [isOpenChildrenSurvey, setOpenChildrenSurvey] = useState(false);
  return (
    <>
      <div className="sidebar-container">
        <div className="sidebar-wrapper">
          {getStorageItem("me")?.agency?.name ? (
            <h1 className="logo-text text-uppercase">SMARTBEAT</h1>
          ) : (
            <img src={Logo} alt="SMARTBEAT" />
          )}
        </div>
        {LinkItems.map(({ link, name, icon, children }) => (
          <div key={name}>
            {link && !children ? (
              <LinkRouter
                className={`${getClasses({
                  "disabled-link": pathname === link,
                })}`}
                key={name}
                to={link}
              >
                <div
                  className={`nav-link-custom menu-item-blue ${
                    pathname.includes(link) ? "active" : ""
                  }`}
                  key={name}
                >
                  {typeof icon === "string" ? (
                    <i className={`icon ${icon}`}></i>
                  ) : (
                    icon
                  )}
                  <span className="title-custom">{name}</span>
                </div>
              </LinkRouter>
            ) : (
              <div>
                <div
                  className={`${getClasses({
                    "disabled-link":
                      children &&
                      children.some(({ link }) => pathname.includes(link)),
                  })}`}
                  key={name}
                  onClick={() => {
                    children &&
                      !children.some(({ link }) => pathname.includes(link)) &&
                      setOpenChildrenSurvey((oldValue) => !oldValue);
                  }}
                >
                  <div
                    className={`nav-link-custom menu-item-blue ${getClasses({
                      "with-children": Boolean(children),
                    })} ${
                      children &&
                      children.some(({ link }) => pathname.includes(link))
                        ? "active"
                        : ""
                    }`}
                    key={name}
                  >
                    <div className="wrapper-icon-title">
                      {typeof icon === "string" ? (
                        <i className={`icon ${icon}`}></i>
                      ) : (
                        icon
                      )}
                      <span className="title-custom">{name}</span>
                    </div>
                    {children && (
                      <Chevron
                        direction={
                          isOpenChildrenSurvey ||
                          children.some(({ link }) => pathname.includes(link))
                            ? "top"
                            : "bottom"
                        }
                      />
                    )}
                  </div>
                </div>
                <div
                  className={`wrapper-children-links ${getClasses({
                    open:
                      isOpenChildrenSurvey ||
                      (children &&
                        children.some(({ link }) => pathname.includes(link))),
                  })}`}
                >
                  {children &&
                    children.map(({ link, name, icon }) => (
                      <LinkRouter
                        className={`${getClasses({
                          "disabled-link": pathname === link,
                        })} `}
                        key={name}
                        to={link}
                      >
                        <div
                          className={`nav-link-custom menu-item-blue ${
                            pathname.includes(link) ? "active" : ""
                          }`}
                          key={name}
                        >
                          {typeof icon === "string" && icon !== "" ? (
                            <i className={`icon ${icon}`}></i>
                          ) : (
                            icon
                          )}
                          <span
                            className={`title-custom ${
                              icon === "" ? "children" : ""
                            }`}
                          >
                            {name}
                          </span>
                        </div>
                      </LinkRouter>
                    ))}
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </>
  );
};
interface MobileProps {
  currentLanguage: string;
  setLanguage: React.Dispatch<React.SetStateAction<string>>;
}

const MobileNav = ({ setLanguage, currentLanguage }: MobileProps) => {
  const { pathname } = useLocation();
  const [{ link, parameters, icon }, setCurrentRoot] = useState<{
    link: string;
    icon: string | JSX.Element;
    parameters: Parameters[];
  }>({ parameters: [], link: "", icon: "" });
  const { t } = useTranslation();
  const {
    ref: refLanguage,
    isComponentVisible: isComponentVisibleLanguage,
    setIsComponentVisible: setIsComponentVisibleLanguage,
  } = useComponentVisible(false);
  const {
    ref: refAvatar,
    isComponentVisible: isComponentVisibleAvatar,
    setIsComponentVisible: setIsComponentVisibleAvatar,
  } = useComponentVisible(false);

  const changeLanguage = (language: "it" | "en") => {
    i18n.changeLanguage(language);
    setLanguage(language);
    setStorageItem("language", language);
    setIsComponentVisibleLanguage(false);
  };

  const getCurrentRoot = useCallback((): {
    parameters: Parameters[];
    link: string;
    icon: string | JSX.Element;
  } => {
    return setHeaderRoots(pathname.split("/"));
  }, [pathname]);

  useEffect(() => {
    setCurrentRoot(getCurrentRoot());
  }, [pathname, getCurrentRoot]);
  const navigate = useNavigate();

  const logout = () => {
    localStorage.clear();
    navigate(RoutesRouter.login);
  };
  useEffect(() => {});

  return (
    <div className="mobile-nav-container">
      {parameters.length > 1 ? (
        <div className="section-left">
          <div className="left">
            {typeof icon === "string" ? (
              <i className={`icon ${icon}`}></i>
            ) : (
              icon
            )}
            <span
              className="text ms-2 link"
              onClick={() => {
                navigate(`/home/${link}`);
              }}
            >
              {t(`home.${parameters[0]}`)} -{" "}
            </span>

            <span className="text ms-1">{t(`home.${parameters[1]}`)}</span>

            {parameters[2] && (
              <span className="text ms-1"> - {t(`home.${parameters[2]}`)}</span>
            )}
          </div>
        </div>
      ) : (
        <div className="section-left">
          <div className="left">
            {typeof icon === "string" ? (
              <i className={`icon ${icon}`}></i>
            ) : (
              icon
            )}
            <span className="text ms-2">{t(`home.${parameters[0]}`)}</span>
          </div>
        </div>
      )}
      <div className="section-right">
        {/* <img className="bell-icons" src={BellNotifications} alt="" /> */}

        <div className="wrapper-section-right">
          <div ref={refLanguage} className="language-container">
            <div
              className="language-wrapper"
              onClick={() => {
                setIsComponentVisibleLanguage((oldValue) => !oldValue);
              }}
            >
              <span className="language-menu capitalize-first-letter">
                {currentLanguage}
              </span>
              <img
                src={LanguageIcon}
                className="language-arrow"
                alt="language arrow"
              ></img>
              <i className="icon icon-ch-down"></i>
            </div>
            {isComponentVisibleLanguage && (
              <div className="switch-language">
                {(["it", "en"] as const).map((language) => (
                  <div className="language-item" key={language}>
                    <div
                      onClick={() => changeLanguage(language)}
                      className="menu-item d-flex justify-content-between gap-1"
                    >
                      <span>{t(`home.languages.${language}`)}</span>
                      {language === currentLanguage && (
                        <i className="icon icon-checkmark icon-square-checked me-1"></i>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
        <div ref={refAvatar} className="avatar-wrapper">
          <div
            className="avatar-button"
            onClick={() => setIsComponentVisibleAvatar((oldValue) => !oldValue)}
          >
            <div className="avatar avatar-home">{`${
              getStorageItem("me")?.name.charAt(0) || ""
            }${getStorageItem("me")?.surname.charAt(0) || ""}`}</div>
          </div>
          {isComponentVisibleAvatar && (
            <div className="wrapper-menu">
              <div className="item-menu">
                <div className="info d-flex align-items-center">
                  <div className="avatar">{`${
                    getStorageItem("me")?.name.charAt(0) || ""
                  }${getStorageItem("me")?.surname.charAt(0) || ""}`}</div>
                  <span>{`${getStorageItem("me")?.name || ""} ${
                    getStorageItem("me")?.surname || ""
                  }`}</span>
                </div>
                <div className="form-group">
                  <div className="form-control-label">Email</div>
                  <div>{`${getStorageItem("me")?.email || ""}`}</div>
                </div>
                <div onClick={logout} className="menu-item">
                  {t("home.logout")}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Home;
